import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { api } from '../api/client.js';
import {
  useCustomer,
  resetIdentityPreservingOnlineJourney,
} from '../customer-context.js';
import { resolveActionBlockMessage } from '../lib/action-denied.js';
import { readRegistrationOrigin } from '../lib/registration-ipw.js';

/**
 * SCRN 57 — Reset internet password (CCF-318 / #425).
 *
 * The destination screen a broker lands on after PASSING the Broker 24 medium
 * security check (CCF-318). Per the story (Archbold, 30/Jun/26): a broker's
 * log-in is not linked to any accounts, so directing them to the customer
 * dashboard would show a blank screen — instead, once broker security passes,
 * the agent is taken here to reset / issue the broker's internet password.
 *
 * Faithful to the manual-IPW path of the Internet Password screen (SCRN-22 /
 * CCF-17) — this reuses the already-contracted, RBAC- + verification-gated BFF
 * route `POST /api/security/internet-password` (→ gateway `password/reset`),
 * which is exactly a password RESET, so no new gateway path is invented. The
 * difference from SCRN-22 is the framing (a broker reset destination, not a
 * dashboard-anchored generation) and the absence of the dashboard back-link
 * (there is no account dashboard for a broker login).
 *
 * The shared agent banner lives in the app shell, so this renders only `<main>`.
 */
export function ResetInternetPasswordScreen(): React.JSX.Element {
  const { customer, setCustomer } = useCustomer();
  const navigate = useNavigate();
  const location = useLocation();

  const customerNumber = customer.cif ?? '';
  const cardNumber = customer.onlineCardNumber ?? '';
  const tellerId = customer.tellerId ?? '';
  const hasContext = !!customerNumber && /^\d{4}$/.test(tellerId);

  // #653 (@dylan219) — when reached FROM a registration screen (Business24 /
  // Broker24) the page runs in a SCOPED mode: it shows a Back button that
  // returns to the EXACT originating registration screen (with all answers + the
  // Pass intact — the server-side `medium` verification is NOT cleared, unlike
  // the Done → ID&V path) and offers NO navigation to any other screen. The
  // origin is carried in router state; its absence means the normal
  // broker-security → IPW / direct-visit flow (Done → ID&V), untouched.
  const registrationOrigin = readRegistrationOrigin(location.state);

  // Return to the originating registration screen. Deliberately does NOT reset
  // the customer context or the server-side verification, so the agent lands
  // back on their answers with the Pass still valid — no re-answering, no 2FA.
  function backToRegistration(): void {
    if (!registrationOrigin) return;
    navigate(registrationOrigin.from);
  }

  const [scriptingComplete, setScriptingComplete] = useState(false);
  const [password, setPassword] = useState('');
  const [generated, setGenerated] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [finishing, setFinishing] = useState(false);
  const [doneError, setDoneError] = useState('');

  // #555 — the broker security journey (CCF-318) ends here; clicking Done
  // returns the agent to ID&V (SCRN-01) for the NEXT customer. The prior
  // broker's identity / verification context must be FULLY cleared — both the
  // shared agent banner (client) AND the authoritative server-side verification
  // session (`req.session.verification`), or servicing routes stay authorised
  // for the previous customer until a fresh ID&V (ai-review HIGH). FAIL CLOSED:
  // reset the client banner + navigate ONLY after the BFF confirms the
  // server-side reset (2xx); on failure surface the error inline and keep the
  // agent in place so they can retry (never present a blank, "cleared" UI while
  // the server still holds the prior verification). Mirrors Clear Session
  // (CCF-10) but uses the lightweight `/idv/reset-context` route so it does NOT
  // count against the CCF-10 cap, emit a CCF-112 clear-down event, or release
  // the CCF-13 hold. Per #251 the client reset preserves a live online journey.
  async function done(): Promise<void> {
    if (!/^\d{4}$/.test(tellerId)) {
      // Without a Teller ID we cannot reset the session server-side; refuse
      // rather than fake a local-only reset that leaves the server authorised.
      setDoneError('Cannot finish: no Teller ID for this agent.');
      return;
    }
    setFinishing(true);
    setDoneError('');
    try {
      const { error: apiError, response } = await api.POST(
        '/idv/reset-context',
        { params: { header: { 'X-Teller-Id': tellerId } } },
      );
      if (apiError || !response.ok) {
        setDoneError(
          resolveActionBlockMessage(response.status, apiError) ??
            'Could not clear the session — please retry.',
        );
        return;
      }
    } catch {
      setDoneError('Could not clear the session — please retry.');
      return;
    } finally {
      setFinishing(false);
    }
    setCustomer(resetIdentityPreservingOnlineJourney(customer));
    navigate('/idv');
  }

  async function reset(): Promise<void> {
    if (!hasContext) {
      setError(
        'No verified broker in context. Complete broker security first.',
      );
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const {
        data,
        error: apiError,
        response,
      } = await api.POST('/security/internet-password', {
        params: { header: { 'X-Teller-Id': tellerId } },
        body: { customerNumber, cardNumber },
      });
      if (
        apiError ||
        !data ||
        (data as { Success?: boolean }).Success !== true
      ) {
        // RBAC action-denial (#248): a blocked WRITE returns
        // `403 { error: 'forbidden' }` — surface @dylan219's verbatim copy
        // inline (recoverable; the button stays so the agent can retry). A
        // non-forbidden 403 (the Teller-ID gate's `invalid_teller_id`) keeps
        // its own distinct message.
        const blocked = resolveActionBlockMessage(response.status, apiError);
        if (blocked !== null) {
          setError(blocked);
          return;
        }
        if (response.status === 403) {
          setError('Forbidden: check your Teller ID and permissions.');
          return;
        }
        // #653 — surface the gateway's ACTUAL Code + Message inline beside the
        // button (recoverable — the button stays so the agent can retry), per
        // the standard error-UX convention (@dylan219 #80/#82/#94). This
        // previously fell to a generic "please try again" toast that HID the
        // real reason — e.g. a gateway `1002 "Card is deregistered, number of
        // PAN validation retries exceeded"` looked like a broken button. The BFF
        // returns the gateway verdict as ResultCode/ResultMessage on the 200 body.
        const failBody = (data ?? {}) as {
          ResultCode?: string;
          ResultMessage?: string;
        };
        const rc = failBody.ResultCode?.trim();
        const rm = failBody.ResultMessage?.trim();
        setError(
          rm
            ? `Could not reset the internet password${rc ? ` (${rc})` : ''}: ${rm}`
            : 'Could not reset the internet password. Please try again.',
        );
        return;
      }
      setPassword((data as { Password?: string }).Password ?? '');
      setGenerated(true);
    } catch {
      toast.error('Could not reset the internet password. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main
      className="max-w-[1400px] mx-auto px-12 py-12"
      aria-labelledby="ripw-heading"
    >
      <div className="flex items-center justify-between gap-8 mb-8 flex-wrap">
        <h1
          id="ripw-heading"
          className="text-[24px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-6"
        >
          Reset internet password
        </h1>
      </div>

      <p className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] mb-6 max-w-[800px]">
        {registrationOrigin
          ? 'Registration security complete. Reset the customer’s internet password below and read the new password out to them. Use Back to return to the registration screen with all answers intact.'
          : 'Broker security passed. Reset this broker’s internet password below and read the new password out to the broker.'}
      </p>

      <div className="bg-white border-2 border-[#979c9f] p-6 shadow-sm max-w-[800px]">
        <h2 className="text-[18px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-4">
          Reset internet password
        </h2>
        <p className="text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-2">
          Before I call out the new internet password I need to make you aware
          that:
        </p>
        <p className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] mb-1">
          You need to log in on a PC or laptop for the first time.
        </p>
        <p className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] mb-4">
          The password will remain active for 30 days.
        </p>
        <p className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] mb-4">
          PTSB will never ask you to confirm the full six digits of your
          Personal Access Number, we will only ever ask you for three random
          digits from this number. PTSB will also never contact you by email or
          text message asking you to confirm or update any personal details.
        </p>
        <div className="mt-6 flex items-center gap-3">
          <input
            type="checkbox"
            id="ripw-scripting"
            checked={scriptingComplete}
            onChange={() => setScriptingComplete((c) => !c)}
            className="size-[20px] accent-[#FF703E] cursor-pointer"
          />
          <label
            htmlFor="ripw-scripting"
            className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] cursor-pointer select-none"
          >
            Scripting complete
          </label>
        </div>

        {scriptingComplete && !generated && (
          <div className="mt-6 pt-6 border-t border-[#e5e7eb]">
            <button
              type="button"
              disabled={submitting}
              onClick={() => void reset()}
              className="px-6 py-3 bg-[#FF703E] text-[#1D252C] rounded-[4px] font-['Arial',sans-serif] font-bold text-[16px] hover:bg-[#e55a2a] focus:outline-none focus:ring-4 focus:ring-offset-2 focus:ring-[#005fcc] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Working…' : 'Reset internet password'}
            </button>
          </div>
        )}

        {/* #653 — reached from registration and not yet reset: a Back button so
            the agent can return to the registration screen (answers + Pass
            intact) WITHOUT resetting the internet password. This is the only
            other navigation on the page in registration mode. */}
        {registrationOrigin && !generated && (
          <div className="mt-6 pt-6 border-t border-[#e5e7eb]">
            <button
              type="button"
              onClick={backToRegistration}
              aria-label="Back to registration"
              className="px-6 py-3 bg-white text-[#1D252C] border-2 border-[#1D252C] rounded-[4px] font-['Arial',sans-serif] font-bold text-[16px] hover:bg-[#f3f4f6] focus:outline-none focus:ring-4 focus:ring-offset-2 focus:ring-[#005fcc] transition-all"
            >
              Back
            </button>
          </div>
        )}

        {generated && (
          <div className="mt-6 pt-6 border-t border-[#e5e7eb]">
            <h3 className="text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-3">
              Internet password:
            </h3>
            <p
              className="text-[28px] font-['Arial',sans-serif] font-bold text-[#1d252c] tracking-wider mb-4 font-mono"
              aria-label="New internet password"
            >
              {password}
            </p>
            <p className="text-[14px] font-['Arial',sans-serif] text-[#1d252c] m-0 mb-4">
              This password will take 10 minutes to activate.
            </p>
            <div className="flex gap-4">
              <button
                type="button"
                disabled={submitting}
                onClick={() => void reset()}
                className="px-6 py-3 bg-white text-[#1D252C] border-2 border-[#1D252C] rounded-[4px] font-['Arial',sans-serif] font-bold text-[16px] hover:bg-[#f3f4f6] focus:outline-none focus:ring-4 focus:ring-offset-2 focus:ring-[#005fcc] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Reset again
              </button>
              {/* #653 — reached from registration: the ONLY other action is Back
                  (returns to the originating registration screen with all
                  answers + the Pass intact). No Done → ID&V, no other nav.
                  Reached the normal way (broker security / direct): Done clears
                  the session and returns to ID&V — unchanged. */}
              {registrationOrigin ? (
                <button
                  type="button"
                  onClick={backToRegistration}
                  aria-label="Back to registration"
                  className="px-6 py-3 bg-[#FF703E] text-[#1D252C] rounded-[4px] font-['Arial',sans-serif] font-bold text-[16px] hover:bg-[#e55a2a] focus:outline-none focus:ring-4 focus:ring-offset-2 focus:ring-[#005fcc] transition-all"
                >
                  Back
                </button>
              ) : (
                <button
                  type="button"
                  disabled={finishing}
                  onClick={() => void done()}
                  className="px-6 py-3 bg-[#FF703E] text-[#1D252C] rounded-[4px] font-['Arial',sans-serif] font-bold text-[16px] hover:bg-[#e55a2a] focus:outline-none focus:ring-4 focus:ring-offset-2 focus:ring-[#005fcc] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Done
                </button>
              )}
            </div>
            {doneError && (
              <p
                role="alert"
                className="mt-3 text-[14px] font-['Arial',sans-serif] text-[#c81e1e] m-0"
              >
                {doneError}
              </p>
            )}
          </div>
        )}

        {/* #653 — reset failures surface here, beside the action button (the
            gateway Code + Message, or the RBAC/Teller-ID copy), recoverable so
            the agent can retry (@dylan219 #80/#82/#94 inline-error UX). */}
        {error && (
          <div
            role="alert"
            className="mt-6 p-4 bg-white border border-[#dc2626] font-['Arial',sans-serif] text-[14px] text-[#1d252c]"
          >
            {error}
          </div>
        )}
      </div>
    </main>
  );
}
